import dayjs from 'dayjs';
import { createRef, useEffect, useMemo, useRef, useState } from 'react';
import DatePicker, {
  type DatePickerProps,
  type ReactDatePickerCustomHeaderProps,
  registerLocale,
} from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { sv } from 'date-fns/locale/sv';
import { da } from 'date-fns/locale/da';
import { fi } from 'date-fns/locale/fi';
import { pl } from 'date-fns/locale/pl';
import { nb } from 'date-fns/locale/nb';
import { shift } from '@floating-ui/react';
import { Paragraph } from '#components/Typography/Paragraph';
import { SimpleTooltip } from '../../../Data/Tooltip/SimpleTooltip';
import {
  DatePickerLabel,
  DatePickerTooltipBottom,
  DatepickerWrapper,
  ErrorMessageBox,
  HighlightedDatesLabel,
  LabelContainer,
  LabelWrapper,
  RequiredIndicator,
} from './styles';
import DatepickerCustomHeader from './subComponents/CustomHeaderDatepicker/CustomHeaderDatepicker';
import CustomInputDatepicker from './subComponents/CustomInputDatepicker/CustomInputDatepicker';
import './datepickerOverride.css';
import { CustomRangeInput } from './subComponents/CustomRangeInput/CustomRangeInput';
import { holidaysPerCountry } from './holidays';
import CustomHeaderMonthpicker from './subComponents/CustomHeaderMonthpicker';
import { CustomCalendarContainer } from './subComponents/CustomCalendarContainer';

registerLocale('sv', sv);
registerLocale('da', da);
registerLocale('fi', fi);
registerLocale('pl', pl);
registerLocale('no', nb);

export type DatepickerProps = Pick<
  DatePickerProps,
  'placeholderText' | 'value' | 'minDate' | 'maxDate' | 'startDate' | 'endDate' | 'selected'
> & {
  monthPicker?: boolean;
  label?: string;
  enabledDates?: string[];
  highlightedDatesLabel?: string;
  highlightDates?: Date[];
  excludeWeekends?: boolean;
  enableRange?: boolean;
  disabled?: boolean;
  required?: boolean;
  isEditable?: boolean;
  showQuickRange?: boolean;
  disabledDates?: string[];
  onRangeChange?: (dates: [Date | null, Date | null]) => void;
  onChange?: (date: Date | null) => void;
  showHolidays?: boolean;
  errorMessage?: string;
  holidaysCountryCode?: 'SE' | 'NO' | 'FI' | 'DK' | 'PL';
  additionalInfoLabel?: string;
  tooltip?: string;
  tooltipBottom?: string;
  allowSameDay?: boolean; // Optional prop to allow same day selection
};

export const Datepicker = (props: DatepickerProps) => {
  const {
    monthPicker,
    enabledDates,
    excludeWeekends,
    label,
    highlightDates,
    enableRange,
    onChange,
    onRangeChange,
    showQuickRange,
    required,
    disabledDates,
    showHolidays,
    errorMessage,
    holidaysCountryCode,
    placeholderText,
    additionalInfoLabel,
    highlightedDatesLabel,
    disabled,
    selected,
    tooltip,
    tooltipBottom,
    allowSameDay,
    ...rest
  } = props;

  const [minDate, setMinDate] = useState<Date | undefined>();
  const [maxDate, setMaxDate] = useState<Date | undefined>();
  const [open, setOpen] = useState(false);
  const { i18n } = useTranslation();
  const datePickerRef = createRef<DatePicker>();
  const inputRef = useRef(null);
  const resolvedLanguage = i18n.resolvedLanguage ?? 'en';

  const filterDate = useMemo(() => {
    if (enabledDates || excludeWeekends || disabledDates) {
      const customDay = (date: Date) => {
        const dateStr = dayjs(date).format('YYYY-MM-DD');

        if (enabledDates && !enabledDates.includes(dateStr)) {
          return false;
        }
        if (disabledDates?.includes(dateStr)) {
          return false;
        }
        if (excludeWeekends && (date.getDay() === 0 || date.getDay() === 6)) {
          return false;
        }

        return true;
      };

      return customDay;
    } else {
      return undefined;
    }
  }, [enabledDates, excludeWeekends, disabledDates]);

  useEffect(() => {
    setMinDate(rest.minDate);
    setMaxDate(rest.maxDate);
    if (enabledDates && enabledDates.length > 0) {
      if (!rest.minDate) {
        const minDate = enabledDates[0];

        setMinDate(dayjs(minDate).toDate());
      }
      if (!rest.maxDate) {
        const maxDate = enabledDates[enabledDates.length - 1];

        setMaxDate(dayjs(maxDate).toDate());
      }
    }
  }, [enabledDates, rest.minDate, rest.maxDate]);

  // Keep memoized - react-datepicker compares this by reference, so a new array
  // every render puts it in a setState loop.
  const highlightedDates = useMemo(() => {
    if (highlightDates?.length) {
      return [
        {
          'react-datepicker__day--highlighted-stena-elipse': highlightDates,
        },
      ];
    }

    return undefined;
  }, [highlightDates]);

  const holidays = useMemo(() => {
    if (showHolidays && holidaysCountryCode) {
      return holidaysPerCountry[holidaysCountryCode].filter((holiday) => {
        return (
          dayjs(holiday.date).isAfter(dayjs()) &&
          dayjs(holiday.date).isBefore(dayjs().add(2, 'year')) // only show holidays for the next 2 years because of limitation in the datepicker
        );
      });
    }

    return undefined;
  }, [showHolidays, holidaysCountryCode]);

  const selectedDateIsHoliday = useMemo(() => {
    if (selected && holidays && !enableRange) {
      const selectedDate = dayjs(selected);

      return holidays.some((holiday) => {
        return dayjs(holiday.date).isSame(selectedDate, 'day');
      });
    }

    return false;
  }, [selected, holidays, enableRange]);

  const toggleOpen = () => {
    datePickerRef.current?.setOpen(!open);
    setOpen(!open);
  };

  const getDatepicker = () => {
    const sharedProps = {
      showMonthYearPicker: !!monthPicker,
      formatWeekDay: (name: string) => name.substring(0, 3),
      calendarStartDay: 1 as const,
      locale: resolvedLanguage,
      dateFormat: 'yyyy-MM-dd',
      filterDate: filterDate,
      minDate: minDate,
      maxDate: maxDate,
      highlightDates: highlightedDates,
      renderCustomHeader: monthPicker
        ? (props: ReactDatePickerCustomHeaderProps) => (
            <CustomHeaderMonthpicker locale={resolvedLanguage} {...props} />
          )
        : (props: ReactDatePickerCustomHeaderProps) => (
            <DatepickerCustomHeader locale={resolvedLanguage} {...props} />
          ),
      renderDayContents: (day: number) => {
        return <span className="react-datepicker__day--inner">{day}</span>;
      },
      holidays,
      ref: datePickerRef,
      onCalendarClose: () => {
        setOpen(false);
      },
      popperPlacement: 'bottom-end' as const,
      popperModifiers: [shift({ crossAxis: true, padding: 15 })],
      disabled,
      selected,
      calendarContainer: CustomCalendarContainer(
        (dates) => {
          if (Array.isArray(dates) && onRangeChange) {
            datePickerRef.current?.setOpen(false);
            onRangeChange(dates);
          }
        },
        showQuickRange,
        monthPicker,
      ),
    };

    if (enableRange) {
      const onRangeChangeHandler = (date: [Date | null, Date | null]) => {
        if (onRangeChange) {
          onRangeChange(date);
        }
      };

      return (
        <DatePicker
          {...sharedProps}
          startDate={rest.startDate}
          endDate={rest.endDate}
          onChange={onRangeChangeHandler}
          customInput={
            <CustomRangeInput
              value={rest.value}
              toggleOpen={toggleOpen}
              updateRange={onRangeChange}
              minDate={rest.minDate}
              maxDate={rest.maxDate}
              disabled={disabled}
              allowSameDay={allowSameDay}
            />
          }
          selectsRange
        >
          {highlightDates && (
            <div>
              <HighlightedDatesLabel>{highlightedDatesLabel}</HighlightedDatesLabel>
            </div>
          )}
          {additionalInfoLabel && <Paragraph variant="large">{additionalInfoLabel}</Paragraph>}
        </DatePicker>
      );
    } else {
      return (
        <DatePicker
          {...sharedProps}
          disabled={disabled}
          onChange={(date: Date | null) => {
            onChange?.(date);
          }}
          customInput={
            <CustomInputDatepicker
              ref={inputRef}
              selectedDateIsHoliday={selectedDateIsHoliday}
              disabled={disabled}
              placeholderText={placeholderText}
            />
          }
        >
          {highlightDates && (
            <div>
              <HighlightedDatesLabel>{highlightedDatesLabel}</HighlightedDatesLabel>
            </div>
          )}
          {additionalInfoLabel && <Paragraph variant="large">{additionalInfoLabel}</Paragraph>}
        </DatePicker>
      );
    }
  };

  return (
    <DatepickerWrapper>
      <LabelContainer>
        {label && (
          <LabelWrapper>
            <DatePickerLabel>{label}</DatePickerLabel>
            {required && <RequiredIndicator>*</RequiredIndicator>}
          </LabelWrapper>
        )}
        {tooltip && <SimpleTooltip>{tooltip}</SimpleTooltip>}
      </LabelContainer>
      {getDatepicker()}
      {!!errorMessage && <ErrorMessageBox>{errorMessage}</ErrorMessageBox>}
      {tooltipBottom && <DatePickerTooltipBottom>{tooltipBottom}</DatePickerTooltipBottom>}
    </DatepickerWrapper>
  );
};
